/**
 * formatExpirationDate
 * @param date
 * Returns a date in format MM/YY
 */
export const formatToMMYY = (date: Date) => {
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = String(date.getUTCFullYear()).slice(2)
  return `${month}/${year}` as const
}

/**
 * formatToTextMonth
 * @param date
 * @param options
 * Returns a date in format Month Day where Month is the full name of the month
 * @example September 9
 **/
export function formatToTextMonth(
  date: Date,
  options: Parameters<typeof date.toLocaleDateString>[1] = { timeZone: 'UTC' },
) {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', ...options })
}

/**
 * formatToMonthYear
 * @param date
 * Returns a date in format Month Year where Month is the full name of the month and Year is the full year
 * @example September 2024
 */
export function formatToMonthYear(date: Date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** @example Aug 16, 2024 */
export function formatDateToShortString(
  date: Date,
  options?: Parameters<typeof date.toLocaleDateString>[1],
) {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  })
}

/** @example December 20, 2024 */
export function formatDateToLongString(date: Date) {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

/** @example 12-30-2025 */
export function formatDate(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}-${day}-${year}` as const
}

/**
 * @example 1st, 2nd, 3rd, 4th, etc.
 * @param date
 * @returns
 */
export function formatToDayWithSuffix(date: Date) {
  const day = date.getUTCDate()
  const suffix = getOrdinalSuffix(day)
  return `${day}${suffix}` as const
}

export function getOrdinalSuffix(day: number) {
  if (day >= 11 && day <= 13) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

/**
 * @example
 * ```ts
 * arrayOfDateStrings.sort(sortByTimeDescending)
 * ```
 */
export function sortByTimeDescending(dateStringA: string, dateStringB: string) {
  return Date.parse(dateStringB) - Date.parse(dateStringA)
}
